(() => {
  if (customElements.get('size-tools')) return;

  class SizeTools extends HTMLElement {
    constructor() {
      super();
      this.addEventListener('click', (event) => this.handleClick(event));
    }

    openChart(trigger) {
      if (this.chartPending) return;
      this.chartPending = true;
      trigger.setAttribute('aria-busy', 'true');
      const moduleUrl = new URL(trigger.dataset.moduleUrl, document.baseURI).href;
      window.__inewerNativeSizeChartModule = window.__inewerNativeSizeChartModule || import(moduleUrl);
      window.__inewerNativeSizeChartModule
        .then((module) => {
          if (this.isConnected && trigger.isConnected) module.openNativeSizeChart(trigger);
        })
        .catch((error) => {
          console.error('Native Size Chart failed to load', error);
          window.__inewerNativeSizeChartModule = null;
          if (this.isConnected) window.alert(this.dataset.chartError);
        })
        .finally(() => {
          this.chartPending = false;
          trigger.removeAttribute('aria-busy');
        });
    }

    openFindMySize(trigger) {
      if (this.findPending) return;
      this.findPending = true;
      const moduleUrl = new URL(trigger.dataset.moduleUrl, document.baseURI).href;
      window.__inewerFindMySizeModule = window.__inewerFindMySizeModule || import(moduleUrl);
      window.__inewerFindMySizeModule
        .then((module) => {
          if (this.isConnected && trigger.isConnected) module.openFindMySize(trigger);
        })
        .catch((error) => {
          console.error('Find My Size failed to load', error);
          window.__inewerFindMySizeModule = null;
          if (this.isConnected) window.alert(this.dataset.findError);
        })
        .finally(() => {
          this.findPending = false;
        });
    }

    handleClick(event) {
      const chart = event.target.closest('[data-inewer-size-chart]');
      if (chart && this.contains(chart)) {
        event.preventDefault();
        this.openChart(chart);
        return;
      }
      const trigger = event.target.closest('[data-inewer-find-size]');
      if (!trigger || !this.contains(trigger)) return;
      event.preventDefault();
      this.openFindMySize(trigger);
    }
  }

  customElements.define('size-tools', SizeTools);

  let nativeTooltip;
  let tooltipAnchor;
  let tooltipPinned = false;

  const labelFromTarget = (target) => {
    const label = target.closest('.inewer-size-option label[for]');
    if (label) return label;
    const input = target.closest('.inewer-size-option input[type="radio"]');
    return input?.id
      ? input.closest('product-info')?.querySelector(`label[for="${CSS.escape(input.id)}"]`)
      : null;
  };

  const sizeRowFor = (label) => {
    const productInfo = label?.closest('product-info');
    const input = label && productInfo?.querySelector(`#${CSS.escape(label.htmlFor)}`);
    const source = productInfo?.querySelector('[data-inewer-size-data]');
    if (!input || input.value === 'Custom Size' || !source) return null;
    try {
      const rows = JSON.parse(source.textContent);
      return Array.isArray(rows) ? rows.find((row) => row.size === input.value) : null;
    } catch (error) {
      console.error('Native size tooltip data could not be read', error);
      return null;
    }
  };

  const getTooltip = () => {
    if (nativeTooltip?.isConnected) return nativeTooltip;
    nativeTooltip = document.createElement('div');
    nativeTooltip.id = 'InewerNativeSizeTooltip';
    nativeTooltip.className = 'inewer-native-size-tooltip';
    nativeTooltip.setAttribute('role', 'tooltip');
    nativeTooltip.hidden = true;
    document.body.appendChild(nativeTooltip);
    return nativeTooltip;
  };

  const renderTooltip = (tooltip, row) => {
    tooltip.replaceChildren();
    const header = document.createElement('div');
    header.className = 'inewer-native-size-tooltip__header';
    const size = document.createElement('strong');
    size.textContent = row.size;
    const unit = document.createElement('span');
    unit.textContent = 'INCH';
    header.append(size, unit);
    const list = document.createElement('dl');
    [
      ['Bust', row.bust],
      ['Waist', row.waist],
      ['Hips', row.hips],
      ['Hollow to Floor', row.hollowToFloor],
    ].forEach(([label, value]) => {
      const term = document.createElement('dt');
      const detail = document.createElement('dd');
      term.textContent = label;
      detail.textContent = value;
      list.append(term, detail);
    });
    tooltip.append(header, list);
  };

  const positionTooltip = () => {
    if (!tooltipAnchor || !nativeTooltip || nativeTooltip.hidden) return;
    const anchorRect = tooltipAnchor.getBoundingClientRect();
    const tooltipRect = nativeTooltip.getBoundingClientRect();
    const left = Math.min(
      window.innerWidth - tooltipRect.width / 2 - 8,
      Math.max(tooltipRect.width / 2 + 8, anchorRect.left + anchorRect.width / 2)
    );
    let top = anchorRect.top - tooltipRect.height - 10;
    const below = top < 8;
    if (below) top = anchorRect.bottom + 10;
    nativeTooltip.classList.toggle('is-below', below);
    nativeTooltip.style.left = `${left}px`;
    nativeTooltip.style.top = `${top}px`;
  };

  const showTooltip = (label, pin = false) => {
    const row = sizeRowFor(label);
    if (!row) return;
    const tooltip = getTooltip();
    if (tooltipAnchor && tooltipAnchor !== label) tooltipAnchor.removeAttribute('aria-describedby');
    tooltipAnchor = label;
    tooltipPinned = pin;
    renderTooltip(tooltip, row);
    tooltip.hidden = false;
    label.setAttribute('aria-describedby', tooltip.id);
    positionTooltip();
  };

  const hideTooltip = (force = false) => {
    if (!nativeTooltip || (tooltipPinned && !force)) return;
    tooltipAnchor?.removeAttribute('aria-describedby');
    tooltipAnchor = null;
    tooltipPinned = false;
    nativeTooltip.hidden = true;
  };

  document.addEventListener('pointerover', (event) => {
    const label = labelFromTarget(event.target);
    if (label) showTooltip(label);
  });
  document.addEventListener('pointerout', (event) => {
    const label = labelFromTarget(event.target);
    if (label && !label.contains(event.relatedTarget)) hideTooltip();
  });
  document.addEventListener('focusin', (event) => {
    const label = labelFromTarget(event.target);
    if (label) showTooltip(label);
  });
  document.addEventListener('focusout', (event) => {
    if (labelFromTarget(event.target)) hideTooltip();
  });
  document.addEventListener('click', (event) => {
    const label = labelFromTarget(event.target);
    if (label) {
      window.requestAnimationFrame(() => showTooltip(label, true));
      return;
    }
    if (!event.target.closest('.inewer-native-size-tooltip')) hideTooltip(true);
  });
  window.addEventListener('resize', () => hideTooltip(true));
  window.addEventListener('scroll', () => hideTooltip(true), true);
})();
